// src/pages/inventory/ManageTax.js
import React, { useState, useEffect, Fragment, useRef } from "react";
import { Card, Col, Table, Input, CardBody, Button } from "reactstrap";
import { FormLabel, Modal } from "react-bootstrap";
import { PlusCircle } from "react-feather";
import { Breadcrumbs, ToolTip } from "../../AbstractElements";
import MyPagination from "../pagination";
import { useTranslation } from "react-i18next";
import { useLoader } from "../../_helper/LoaderContext";
import { usePharmacy } from "../../_helper/PharmacyProvider";
import { toast } from "react-toastify";

// API functions (to be created in TaxService.js)
import {
  getTaxesAPI,
  createTaxAPI,
  updateTaxAPI,
  deleteTaxAPI,
} from "./TaxService";
import { FaPlusCircle } from "react-icons/fa";
import { useDisableScrollOnFocus } from "../../Hooks/useDisableScrollOnFocus";

const ManageTax = () => {
  const [taxes, setTaxes] = useState([]);
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const { setLoading } = useLoader();
  const { t } = useTranslation();
  const { pharmacyPOS } = usePharmacy();

  const editButtonRefs = useRef({});
  const deleteButtonRefs = useRef({});
  const disableScrollProps = useDisableScrollOnFocus();

  const [editTooltipOpen, setEditTooltipOpen] = useState({});
  const [deleteTooltipOpen, setDeleteTooltipOpen] = useState({});

  const toggleEditTooltip = (id) => {
    setEditTooltipOpen((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const toggleDeleteTooltip = (id) => {
    setDeleteTooltipOpen((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const [showModal, setShowModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [taxId, setTaxId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTaxId, setDeleteTaxId] = useState(null);

  const [currentTax, setCurrentTax] = useState({
    taxName: "",
    amount: "",
  });

  // Fetch taxes
  useEffect(() => {
    getTaxes();
  }, [page, size]);

  const getTaxes = async () => {
    const body = {
      clinicId: pharmacyPOS?.hospital_id,
      page,
      limit: size,
    };
    try {
      setLoading(true);
      const response = await getTaxesAPI(body);
      setTaxes(response.data.data.taxes || []);
      const totalCount = response.data.data.total || 0;
      setTotalItems(Math.ceil(totalCount / size));
    } catch (error) {
      console.error("Error fetching taxes:", error);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (pageNumber) => setPage(pageNumber);
  const handlePageSizeChange = (e) => {
    setSize(parseInt(e.target.value));
    setPage(1);
  };

  // Open Add/Edit Modal
  const openModal = (tax = null) => {
    if (tax) {
      setTaxId(tax._id);
      setCurrentTax({ taxName: tax.taxName, amount: tax.tax });
      setIsEdit(true);
    } else {
      setCurrentTax({ taxName: "", amount: "" });
      setIsEdit(false);
    }
    setShowModal(true);
  };

  // Save Tax
  const handleSaveTax = async () => {
    try {
      setLoading(true);
      if (isEdit) {
        const payload = {
          clinicId: pharmacyPOS?.hospital_id,
          taxId,
          taxName: currentTax.taxName,
          tax: currentTax.amount,
        };
        const response = await updateTaxAPI(payload);
        if (response.data.status === "success") {
          toast.success(t(response.data.message));
        }
      } else {
        const payload = {
          clinicId: pharmacyPOS?.hospital_id,
          pharmacyId: pharmacyPOS?._id,
          taxName: currentTax.taxName,
          tax: currentTax.amount,
        };
        const response = await createTaxAPI(payload);
        if (response.data.status === "success") {
          toast.success(t(response.data.message));
        }
      }
      setShowModal(false);
      getTaxes();
    } catch (error) {
      console.error("Error saving tax:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTax = async (taxId) => {
    setLoading(true);
    try {
      const body = { taxId: taxId };
      const response = await deleteTaxAPI(body);
      if (response.data.status === "success") {
        toast.success(t(response.data.message));
        getTaxes();
      } else {
        toast.warning(t(response.data.message));
      }
    } catch (error) {
      console.error("Error deleting tax:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Fragment>
      <Breadcrumbs
        mainTitle={t("Manage Taxes")}
        parent={t("Dashboard")}
        title={t("Taxes")}
      />

      <Col sm="12">
        {/* Tax List */}
        <Card>
          <CardBody>
            <div className="mb-3 d-flex justify-content-between">
              <h5>{t("All Taxes")}</h5>
              <Button color="primary" onClick={() => openModal()}>
                <FaPlusCircle size={16} /> {t("Add Tax")}
              </Button>
            </div>

            <div className="table-responsive">
              <Table bordered hover striped>
                <thead>
                  <tr className="text-center">
                    <th>#</th>
                    <th>{t("Tax Name")}</th>
                    <th>{t("Tax Amount")} (%)</th>
                    <th>{t("Action")}</th>
                  </tr>
                </thead>
                <tbody>
                  {taxes.length > 0 ? (
                    taxes.map((tax, idx) => (
                      <tr key={tax._id} className="text-center">
                        <td>{(page - 1) * size + idx + 1}</td>
                        <td>{tax.taxName}</td>
                        <td>{tax.tax}</td>
                        <td>
                          <button
                            id={`edit-${tax._id}`}
                            ref={(el) => (editButtonRefs.current[tax._id] = el)}
                            className="success-action-button me-1"
                            onClick={() => {
                              if (editButtonRefs.current[tax._id]) {
                                editButtonRefs.current[tax._id].blur();
                              }
                              setEditTooltipOpen((prev) => ({
                                ...prev,
                                [tax._id]: false,
                              }));
                              openModal(tax);
                            }}
                          >
                            <i
                              className="fa fa-pencil-square"
                              style={{ fontSize: "18px" }}
                            ></i>
                            <ToolTip
                              attrToolTip={{
                                placement: "top",
                                isOpen: editTooltipOpen[tax._id] || false,
                                target: `edit-${tax._id}`,
                                toggle: () => toggleEditTooltip(tax._id),
                              }}
                            >
                              <strong>{t("Edit")}</strong>
                            </ToolTip>
                          </button>

                          <button
                            id={`delete-${tax._id}`}
                            ref={(el) =>
                              (deleteButtonRefs.current[tax._id] = el)
                            }
                            className="danger-action-button me-1"
                            onClick={() => {
                              if (deleteButtonRefs.current[tax._id]) {
                                deleteButtonRefs.current[tax._id].blur();
                              }
                              setEditTooltipOpen((prev) => ({
                                ...prev,
                                [tax._id]: false,
                              }));
                              setDeleteTaxId(tax._id);
                              setShowDeleteModal(true);
                            }}
                          >
                            <i
                              className="fa fa-trash"
                              style={{ fontSize: "18px" }}
                            ></i>
                            <ToolTip
                              attrToolTip={{
                                placement: "top",
                                isOpen: deleteTooltipOpen[tax._id] || false,
                                target: `delete-${tax._id}`,
                                toggle: () => toggleDeleteTooltip(tax._id),
                              }}
                            >
                              <strong>{t("Delete")}</strong>
                            </ToolTip>
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" className="p-3 text-center text-muted">
                        {t("No taxes found")}
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>

            {/* Pagination */}
            <div className="mt-3 d-md-flex align-items-center justify-content-between">
              <div className="show-entries">
                <FormLabel>{t("Show")}&nbsp;</FormLabel>
                <select
                  value={size}
                  onChange={handlePageSizeChange}
                  className="form-control"
                  style={{ display: "inline", width: "auto" }}
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                </select>
                &nbsp;<FormLabel>{t("entries")}</FormLabel>
              </div>
              <MyPagination
                totalPages={totalItems}
                currentPage={page}
                onPageChange={handlePageChange}
              />
            </div>
          </CardBody>
        </Card>
      </Col>

      {/* Add / Update Tax Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{isEdit ? t("Update Tax") : t("Add Tax")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <label className="form-label">{t("Tax Name")}</label>
            <Input
              type="text"
              value={currentTax.taxName}
              onChange={(e) =>
                setCurrentTax({ ...currentTax, taxName: e.target.value })
              }
              placeholder={t("Ex: VAT, GST")}
            />
          </div>
          <div className="mb-3">
            <label className="form-label">{t("Tax Amount")} (%)</label>
            <Input
              type="number"
              value={currentTax.amount}
              min={0}
              step={.01}
              max={100}
              onChange={(e) =>
                setCurrentTax({ ...currentTax, amount: e.target.value })
              }
              {...disableScrollProps}
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button color="secondary" onClick={() => setShowModal(false)}>
            {t("Cancel")}
          </Button>
          <Button color="primary" onClick={handleSaveTax}>
            {t("Submit")}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Tax Modal */}
      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{t("Confirm Delete")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{t("Are you sure you want to delete this tax?")}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button color="secondary" onClick={() => setShowDeleteModal(false)}>
            {t("Cancel")}
          </Button>
          <Button
            color="danger"
            onClick={() => {
              handleDeleteTax(deleteTaxId);
              setShowDeleteModal(false);
            }}
          >
            {t("Delete")}
          </Button>
        </Modal.Footer>
      </Modal>
    </Fragment>
  );
};

export default ManageTax;
